import {
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";

import { AssociationRecord } from "@/shared/domain/association";
import { basename } from "@/shared/domain/files/filesystem";
import { useFile } from "@/shared/domain/files/hooks/useFile";
import { MessagePathRecord } from "@/shared/domain/topics";
import { useTopicById } from "@/shared/domain/topics/hooks/useTopic";
import { Goto } from "@/shared/navigation";

import { RobotoLinkHoverUnderline } from "../RobotoLinkHoverUnderline";
import { RobotoTypography } from "../RobotoTypography";

import { Cell } from "./Cell";
import styles from "./Cell.module.css";
import { MessagePathRow } from "./MessagePathRow";

interface MessagePathDetailProps {
  topicId: string;
  topicAssociation?: AssociationRecord;
  messagePathIds?: string[];
  showTitle?: boolean;
}

/**
 * A component used as a sub-table to list message paths accessible from other resources, such as file metadata or topics.
 */
export function MessagePathsDetail({
  topicId,
  topicAssociation,
  messagePathIds,
  showTitle = true,
}: MessagePathDetailProps) {
  const theme = useTheme();

  // Get the complete topic record incl. message paths
  const topicRecordQuery = useTopicById(topicId);

  // Get the complete file record incl. filename
  const association = topicAssociation ?? topicRecordQuery.data?.association;
  const fileRecordQuery = useFile(association?.association_id);

  if (topicRecordQuery.isLoading || fileRecordQuery.isLoading) {
    return <CircularProgress size="1.5rem" />;
  } else if (topicRecordQuery.error) {
    return (
      <Alert severity="error">Failed to fetch message paths for topic</Alert>
    );
  } else if (topicRecordQuery.data && association && fileRecordQuery.data) {
    const topic = topicRecordQuery.data;

    const rows = topic.message_paths
      .filter((messagePath: MessagePathRecord) =>
        messagePathIds
          ? messagePathIds.includes(messagePath.message_path_id)
          : true,
      )
      .map((messagePath: MessagePathRecord) => (
        <MessagePathRow
          key={messagePath.message_path_id}
          messagePath={messagePath}
          topicRecord={topic}
          topicAssociation={association}
        />
      ));

    return (
      <>
        <RobotoTypography
          variant="body2"
          marginBottom={theme.spacing(2)}
          color={"rgb(107, 114, 128)"}
          display={showTitle ? "block" : "none"}
        >
          <span
            style={{
              fontWeight: 500,
            }}
          >
            {topic.topic_name}
          </span>{" "}
          in{" "}
          <RobotoLinkHoverUnderline
            to={Goto.createFileRoute(fileRecordQuery.data.file_id)}
          >
            {basename(fileRecordQuery.data.relative_path)}
          </RobotoLinkHoverUnderline>
        </RobotoTypography>
        <TableContainer>
          <Table size="small" sx={{ width: "auto" }}>
            <TableHead>
              <TableRow>
                <Cell className={styles.header}>Message Paths</Cell>
                <Cell className={styles.header}>Type</Cell>
                <Cell className={styles.header}>Min</Cell>
                <Cell className={styles.header}>Max</Cell>
                <Cell className={styles.header}>Mean</Cell>
                <Cell className={styles.header}>Median</Cell>
                <Cell className={styles.header}>True</Cell>
                <Cell className={styles.header}>False</Cell>
              </TableRow>
            </TableHead>
            <TableBody>{rows}</TableBody>
          </Table>
        </TableContainer>
      </>
    );
  }
  return null;
}
